import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@stores/auth/authStore";
import { authApi } from "@api/modules/auth.api";
import { GlobalLoader } from "@components/loader";
import { OWNER_AUTH_LS_KEY } from "@lib/ownerPersistKeys";
import { isPlatformConsoleRole, normalizeUser } from "@lib/normalizeUser";
import type { BroadcastConfig } from "@lib/broadcast";

const LS_KEY = OWNER_AUTH_LS_KEY;

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        const run = async () => {
            const stored = localStorage.getItem(LS_KEY);

            if (!stored) {
                setIsHydrated(true);
                setIsValid(false);
                return;
            }

            try {
                const parsed = JSON.parse(stored);
                const storedUser = parsed?.state?.user;
                const storedToken = parsed?.state?.token;
                const storedAuth = parsed?.state?.isAuthenticated;
                const storedPerms: string[] = Array.isArray(parsed?.state?.permissions)
                    ? parsed.state.permissions
                    : [];
                const storedBroadcast = parsed?.state?.broadcast as BroadcastConfig | null | undefined;

                if (
                    !storedUser ||
                    !storedToken ||
                    !storedAuth ||
                    !isPlatformConsoleRole(storedUser.role)
                ) {
                    setIsHydrated(true);
                    setIsValid(false);
                    return;
                }

                const currentState = useAuthStore.getState();
                if (!currentState.user || !currentState.token) {
                    useAuthStore.setState({
                        user: normalizeUser(storedUser),
                        token: storedToken,
                        isAuthenticated: true,
                        permissions: storedPerms,
                        broadcast: storedBroadcast ?? null,
                    });
                }

                setIsHydrated(true);
                setIsValid(true);

                authApi
                    .me()
                    .then((res) => {
                        const data = res as unknown as {
                            user?: Record<string, unknown>;
                            permissions?: string[];
                            broadcast?: BroadcastConfig;
                        };
                        const token = useAuthStore.getState().token;
                        if (data?.user && token) {
                            useAuthStore.getState().setAuth(
                                normalizeUser(data.user),
                                token,
                                Array.isArray(data.permissions) ? data.permissions : storedPerms,
                                data.broadcast ?? useAuthStore.getState().broadcast
                            );
                        } else if (data?.broadcast) {
                            useAuthStore.getState().setBroadcast(data.broadcast);
                        }
                    })
                    .catch(() => {});

                authApi
                    .checkAuth()
                    .then((res) => {
                        const data = res as unknown as {
                            user?: Record<string, unknown>;
                            broadcast?: BroadcastConfig;
                        };
                        if (data?.user && isPlatformConsoleRole(data.user.role as string)) {
                            useAuthStore.setState({
                                user: normalizeUser(data.user),
                                token: useAuthStore.getState().token,
                                isAuthenticated: true,
                                broadcast: data.broadcast ?? useAuthStore.getState().broadcast,
                            });
                        } else if (data?.broadcast) {
                            useAuthStore.getState().setBroadcast(data.broadcast);
                        }
                    })
                    .catch(() => {});
            } catch (e) {
                console.error("[ProtectedRoute] Parse error:", e);
                setIsHydrated(true);
                setIsValid(false);
            }
        };

        run();

        const unsubscribe = useAuthStore.subscribe((state) => {
            if (state.user && state.token && state.isAuthenticated) {
                setIsValid(isPlatformConsoleRole(state.user.role));
            } else {
                setIsValid(false);
            }
        });

        return () => unsubscribe();
    }, []);

    if (!isHydrated || isValid === null) {
        return <GlobalLoader />;
    }

    if (!isValid) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
};
